import { OutlinedPrimaryButton } from '@monorepo/shared/componentsV2/Button/outlined/OutlinedPrimaryButton';
import { InputLabel } from '@monorepo/shared/componentsV2/Input';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { USAGE_TRACKING_PREFIX } from '@monorepo/shared/constants/usageTracking';
import { useFileInputHandler } from '@monorepo/shared/hooks/useFileInputHandler';
import { useModal } from '@monorepo/shared/hooks/useModalV2';
import { format } from 'date-fns';
import { FORM_TEMPLATE_GENERATION_FILE_SIZE_LIMIT_IN_MB } from 'mapistry-shared';
import React, { useCallback } from 'react';
import { Form } from 'react-final-form';
import styled from 'styled-components';
import { useFormTemplateContext } from '../EditFormTemplatePage/FormTemplateContext';
import { CustomPromptField } from './CustomPromptField';
import { PdfPreview } from './PdfPreview';

// this could / should be a Mui input, styling this got de-prioritized for speed (July 2025)
const StyledFileInput = styled.input`
  width: 100%;
  margin-top: 0.2rem;
`;

const StyledButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const StyledProcessingInfo = styled.div`
  margin-top: 1rem;
  font-style: italic;
`;

export type HandleUploadPdf = ({
  file,
  customPrompt,
}: {
  file?: File;
  customPrompt?: string;
}) => void;

export function PdfUploader({
  handleUpload,
  customPrompt,
  setCustomPrompt,
  isProcessing,
  startedProcessingAt,
}: {
  handleUpload: HandleUploadPdf;
  customPrompt?: string;
  setCustomPrompt: (customPrompt: string) => void;
  isProcessing: boolean;
  startedProcessingAt?: Date;
}) {
  const { fileData, fileInputChangeHandler } = useFileInputHandler({
    fileSizeLimitInMb: FORM_TEMPLATE_GENERATION_FILE_SIZE_LIMIT_IN_MB,
  });

  // Currently only one file can be uploaded at a time
  const selectedFile = fileData[0];

  const { confirm } = useModal();

  const {
    formTemplateHelpers: { getEqualsDefaultStarterTemplate },
  } = useFormTemplateContext();

  const handleFormUpload = useCallback(async () => {
    const didUserEditFields = !getEqualsDefaultStarterTemplate();
    if (!didUserEditFields) {
      handleUpload({ file: selectedFile?.file, customPrompt });
      return;
    }
    const isConfirmed = await confirm({
      title: 'Process with AI',
      description:
        'Creating a new form with AI will overwrite the current form. Are you sure you want to proceed?',
      danger: true,
    });
    if (isConfirmed) {
      handleUpload({ file: selectedFile?.file, customPrompt });
    }
  }, [
    confirm,
    customPrompt,
    selectedFile?.file,
    getEqualsDefaultStarterTemplate,
    handleUpload,
  ]);

  return (
    <>
      <Form onSubmit={handleFormUpload}>
        {({ handleSubmit }) => (
          <>
            <CustomPromptField
              isProcessing={isProcessing}
              setCustomPrompt={setCustomPrompt}
            />

            <StyledButtons>
              <InputLabel>
                Attach PDF (optional)
                <StyledFileInput
                  type="file"
                  accept="application/pdf"
                  onChange={fileInputChangeHandler}
                  disabled={isProcessing}
                />
              </InputLabel>

              <OutlinedPrimaryButton
                id={`${USAGE_TRACKING_PREFIX}-generate-form-with-ai-button`}
                disabled={(!selectedFile && !customPrompt) || isProcessing}
                type="submit"
                onClick={handleSubmit}
                variant="outlined"
                startIcon={isProcessing ? <Loading size="small" /> : undefined}
              >
                {isProcessing ? 'Processing...' : 'Process with AI'}
              </OutlinedPrimaryButton>
            </StyledButtons>
          </>
        )}
      </Form>

      {startedProcessingAt && (
        <StyledProcessingInfo>
          <div>Started processing at {format(startedProcessingAt, 'pp')}.</div>
          <div>
            This might take a while - you can work in other tabs, but do not
            close this tab or navigate away while it is processing.
          </div>
        </StyledProcessingInfo>
      )}

      {selectedFile && <PdfPreview fileBlobUrl={selectedFile.fileBlobUrl} />}
    </>
  );
}
